import {
  Card,
  Col,
  Row,
  Space,
  Typography,
  message as AntDesignMessage,
} from "antd";
import React from "react";
import { ConnectWalletInstruction } from "../component/ConnectWallet/ConnectWalletInstruction";
import { FarmCard } from "../component/Farm/Farm";
import { Loading } from "../component/Loading/Loading";
import { contracts, divisor, BN } from "../constants";
import { Web3Context } from "../store/Web3Store";

const MASTER_CHEF_ADDRESS = "0x390Dc58E5fa9D5Be0430c28acd2d8aA68D9435ea";
const PANCAKE_LP_ADDRESS = "0xb1196a805611fcc3cde3dd7b1442c7f31dadd03c";

const { Text } = Typography;

export const StakingPage = () => {
  const { web3, account } = React.useContext(Web3Context);
  const [loading, setLoading] = React.useState(true);
  const [bnbPrice, setBnbPrice] = React.useState(null);
  const [stakingData, setStakingData] = React.useState({});

  const getBnbPrice = () => {
    return fetch(
      "https://api.coingecko.com/api/v3/simple/price?ids=binancecoin&vs_currencies=usd"
    )
      .then((res) => res.json())
      .then((data) => {
        setBnbPrice(data.binancecoin.usd);
        return data.binancecoin.usd;
      });
  };

  const getWagmiPrice = async () => {
    const cgBnbPrice = await getBnbPrice();
    const pancakeLpContract = new web3.eth.Contract(
      contracts.pancakeV2LpAbiJson,
      PANCAKE_LP_ADDRESS
    );
    const reserve = await pancakeLpContract.methods.getReserves().call();
    const reserve0 = reserve["_reserve0"];
    const reserve1 = reserve["_reserve1"];
    const wagmiBnbPrice = reserve1 / reserve0;
    const wagmiUsdPrice = wagmiBnbPrice * cgBnbPrice;

    setStakingData((d) => ({
      ...d,
      wagmiBnbPrice,
      wagmiUsdPrice,
    }));
  };

  const handleDeposit = (amount) => () => {
    const masterChefLpContract = new web3.eth.Contract(
      contracts.masterChefAbiJson,
      MASTER_CHEF_ADDRESS
    );
    return masterChefLpContract.methods
      .deposit(0, new BN(amount).times(divisor).toFixed())
      .send({
        from: account,
      })
      .then(() => {
        AntDesignMessage.success(`Successfully deposited!`, 10);
        handleReload();
      })
      .catch((error) => {
        AntDesignMessage.error(error.message, 10);
        handleReload();
      });
  };

  const handleWithdraw = (amount) => () => {
    const masterChefLpContract = new web3.eth.Contract(
      contracts.masterChefAbiJson,
      MASTER_CHEF_ADDRESS
    );
    return masterChefLpContract.methods
      .withdraw(0, new BN(amount).times(divisor).toFixed())
      .send({
        from: account,
      })
      .then(() => {
        AntDesignMessage.success(`Successfully withdrew!`, 10);
        handleReload();
      })
      .catch((error) => {
        AntDesignMessage.error(error.message, 10);
        handleReload();
      });
  };

  const handleApproval = () => {
    const pancakeLpContract = new web3.eth.Contract(
      contracts.pancakeV2LpAbiJson,
      PANCAKE_LP_ADDRESS
    );

    return pancakeLpContract.methods
      .approve(
        MASTER_CHEF_ADDRESS,
        "115792089237316195423570985008687907853269984665640564020457584007913129639935"
      )
      .send({
        from: account,
      })
      .then(() => {
        AntDesignMessage.success("$WAGMI-BNB LP Contract Approved!", 10);
        handleReload();
      })
      .catch((error) => {
        AntDesignMessage.error(error.message, 10);
        handleReload();
      });
  };

  const handleReload = async () => {
    const masterChefLpContract = new web3.eth.Contract(
      contracts.masterChefAbiJson,
      MASTER_CHEF_ADDRESS
    );
    const pancakeLpContract = new web3.eth.Contract(
      contracts.pancakeV2LpAbiJson,
      PANCAKE_LP_ADDRESS
    );

    const wagmiV2BnbLpAmount = new BN(
      await pancakeLpContract.methods.balanceOf(account).call()
    ).div(divisor);
    const userInfo = await masterChefLpContract.methods
      .userInfo(0, account)
      .call();

    const masterChefLpAmount = new BN(userInfo.amount).div(divisor);
    const approveLimit = new BN(
      await pancakeLpContract.methods
        .allowance(account, MASTER_CHEF_ADDRESS)
        .call()
    ).div(divisor);
    const pendingWagmi = new BN(
      await masterChefLpContract.methods.pendingWagmi(0, account).call()
    ).div(divisor);
    const isApprove = wagmiV2BnbLpAmount.lt(approveLimit);
    const wagmiPerBlock = new BN(
      await masterChefLpContract.methods.wagmiPerBlock().call()
    ).div(divisor);
    const totalStakedOfLp = new BN(
      await pancakeLpContract.methods.balanceOf(MASTER_CHEF_ADDRESS).call()
    ).div(divisor);
    const percentOfPoolOwned = masterChefLpAmount.div(totalStakedOfLp);

    setStakingData((d) => ({
      ...d,
      wagmiV2BnbLpAmount: wagmiV2BnbLpAmount.toFixed() || 0,
      masterChefLpAmount: masterChefLpAmount.toFixed() || 0,
      pendingWagmi: pendingWagmi.toFixed(),
      isApprove,
      percentOfPoolOwned: percentOfPoolOwned.toFixed(),
      wagmiPerBlock: wagmiPerBlock.toFixed(),
    }));
  };

  const mainFunc = async () => {
    setLoading(true);
    await getWagmiPrice();
    await handleReload();
    setLoading(false);
  };

  React.useEffect(() => {
    let interval1, interval2;

    if (!!web3) {
      mainFunc();

      setInterval(handleReload, 10000);
      setInterval(getWagmiPrice, 30000);
    }

    return () => {
      interval1 && clearInterval(interval1);
      interval2 && clearInterval(interval2);
    };
  }, [account]);

  if (!web3) {
    return <ConnectWalletInstruction />;
  } else if (loading) {
    return <Loading />;
  }

  return (
    <Row justify="center">
      <Space direction="vertical">
        <Row justify="center">
          <Space>
            <Card>
              <Text strong>$BNB</Text> ${bnbPrice}
            </Card>
            <Card>
              <Text strong>$WAGMI</Text> ${stakingData.wagmiUsdPrice}
            </Card>
          </Space>
        </Row>
        <Row justify="center">
          <FarmCard
            title="WAGMI-BNB LP"
            maxDepositAmount={stakingData.wagmiV2BnbLpAmount}
            maxWithdrawAmount={stakingData.masterChefLpAmount}
            isApprove={stakingData.isApprove}
            pendingReward={stakingData.pendingWagmi}
            depositCoinSymbol="WAGMI-BNB LP"
            withdrawCoinSymbol="WAGMI-BNB LP"
            description="stake WAGMI-BNB Pancake V2 LP and earn $WAGMI (0.5% deposit fee)"
            image={
              <>
                <img
                  className="cover coin-image"
                  src="/wagmi-logo.png"
                  alt="xWagmi"
                />
                <img className="cover coin-image" src="/bnb.png" alt="xWagmi" />
              </>
            }
            details={
              <Row>
                <Col>
                  <Row>
                    <a
                      href="https://bscscan.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Provide Liquidity on PancakeSwap V2
                    </a>
                  </Row>
                  <Row>
                    <a
                      href="https://bscscan.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View on BSCScan
                    </a>
                  </Row>
                </Col>
              </Row>
            }
            deposit={handleDeposit}
            withdraw={handleWithdraw}
            approve={handleApproval}
          />
        </Row>
      </Space>
    </Row>
  );
};
